// src/components/pipelines/FlowCanvasShadcn.jsx - Main React Flow Canvas for Pipeline Automation
import React, { useState, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// Custom Nodes
import KanbanNode from './nodes/KanbanNode';
import LogicGateNode from './nodes/LogicGateNode';
import WaitNode from './nodes/WaitNode';
import EntryNode from './nodes/EntryNode';
import ExitNode from './nodes/ExitNode';

// Overlays
import TopBar from './overlays/TopBar';
import PropertiesPanel from './overlays/PropertiesPanel';

// UI Components
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';

// Hooks
import { usePipelines } from '../../hooks/usePipelines';

// Node types configuration
const nodeTypes = {
  kanban: KanbanNode,
  logicGate: LogicGateNode,
  wait: WaitNode,
  entry: EntryNode,
  exit: ExitNode,
};

// Default edge style (dark theme)
const defaultEdgeOptions = {
  style: { stroke: '#8b5cf6', strokeWidth: 2 },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#8b5cf6',
  },
};

/**
 * FlowCanvasShadcn - Main automation flow canvas using React Flow
 *
 * Features:
 * - Full-screen canvas with floating overlays
 * - Drag-and-drop node creation
 * - Custom node types (Kanban, Logic Gates, Actions)
 * - Horizontal workflow layout
 * - Dark theme with purple accents
 */
const FlowCanvasShadcn = () => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Store connection
  const {
    pipelines,
    pipelinesLoading,
    loadPipelines,
    createPipeline,
    createPipelineStage,
    stages,
    loadStages,
    rules, // Add rules for stage automation counts
    // Flow operations
    flows,
    flowsLoading,
    selectedFlow,
    loadFlows,
    loadFlowDetails,
    createFlow,
    updateFlow,
    saveFlowConfig,
  } = usePipelines();

  // Local state for current flow
  const [currentFlow, setCurrentFlow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isCreatePipelineDialogOpen, setIsCreatePipelineDialogOpen] = useState(false);
  const [pendingKanbanPosition, setPendingKanbanPosition] = useState(null);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [newPipelineDescription, setNewPipelineDescription] = useState('');

  // Use refs to track stable values and prevent feedback loops
  const currentFlowRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const loadedStagesForPipelinesRef = useRef(new Set()); // Track which pipelines have stages loaded

  // Note: Pipelines and flows are auto-loaded by PipelineStore on initialization
  // No need to manually load them here

  // Sync selectedFlow from store to local state ONLY when flow changes (not on auto-save updates)
  React.useEffect(() => {
    if (selectedFlow) {
      // Only update nodes/edges if this is a different flow or we're not auto-saving
      const isDifferentFlow = currentFlowRef.current?.id !== selectedFlow.id;

      if (isDifferentFlow && !isAutoSavingRef.current) {
        console.log('📋 Loading flow configuration:', selectedFlow.name);
        setCurrentFlow(selectedFlow);
        currentFlowRef.current = selectedFlow;

        // Clear loaded stages tracking when switching flows
        loadedStagesForPipelinesRef.current.clear();

        // Load flow configuration (nodes and edges)
        if (selectedFlow.flow_config) {
          const { nodes: flowNodes, edges: flowEdges } = selectedFlow.flow_config;
          setNodes(flowNodes || []);
          setEdges(flowEdges || []);
        } else {
          // Empty flow
          setNodes([]);
          setEdges([]);
        }
      } else if (!isDifferentFlow && !isAutoSavingRef.current) {
        // Same flow, just update metadata without touching nodes/edges
        setCurrentFlow(selectedFlow);
        currentFlowRef.current = selectedFlow;
      }

      // Reset auto-saving flag
      isAutoSavingRef.current = false;
    }
  }, [selectedFlow, setNodes, setEdges]);

  // Auto-load stages for all kanban nodes when flow loads
  React.useEffect(() => {
    // Find all kanban nodes with pipelineIds
    const kanbanNodes = nodes.filter(node => node.type === 'kanban' && node.data?.pipelineId);

    // Extract unique pipeline IDs
    const pipelineIds = [...new Set(kanbanNodes.map(node => node.data.pipelineId))];

    // Load stages for each pipeline (if not already loaded)
    pipelineIds.forEach(pipelineId => {
      if (!loadedStagesForPipelinesRef.current.has(pipelineId)) {
        console.log('📊 Auto-loading stages for pipeline:', pipelineId);
        loadStages(pipelineId);
        loadedStagesForPipelinesRef.current.add(pipelineId);
      }
    });
  }, [nodes, loadStages]);

  // Handle node connection
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge({
        ...params,
        ...defaultEdgeOptions,
      }, eds));
    },
    [setEdges]
  );

  // Handle drag over (allow drop)
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop (create new node)
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');

      if (!data) return;

      const { type, subtype, pipelineId, pipelineData, createNew } = JSON.parse(data);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      // If this is a "create new pipeline" drop, show dialog and store position
      if (type === 'kanban' && createNew) {
        setPendingKanbanPosition(position);
        setIsCreatePipelineDialogOpen(true);
        return;
      }

      // Create new node based on type
      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: getDefaultNodeData(type, subtype, pipelineId, pipelineData),
      };

      setNodes((nds) => nds.concat(newNode));

      // Note: Stages will be auto-loaded by the useEffect that watches nodes
    },
    [reactFlowInstance, setNodes]
  );

  // Get default data for new nodes
  const getDefaultNodeData = (type, subtype, pipelineId, pipelineData) => {
    switch (type) {
      case 'kanban':
        if (pipelineId && pipelineData) {
          // Existing kanban with data passed directly
          return {
            pipelineId,
            name: pipelineData.name || 'Kanban Board',
            stages: pipelineData.stages || [],
            totalCustomers: pipelineData.customer_count || 0,
            isActive: pipelineData.is_active || true,
          };
        } else if (pipelineId) {
          // Existing kanban - find pipeline data from store
          const pipeline = pipelines.find(p => p.id === pipelineId);
          return {
            pipelineId,
            name: pipeline?.name || 'Kanban Board',
            stages: pipeline?.stages || [],
            totalCustomers: pipeline?.customer_count || 0,
            isActive: pipeline?.is_active || true,
          };
        }
        // New kanban (will be set after pipeline creation)
        return {
          name: 'New Kanban',
          stages: [],
          totalCustomers: 0,
          isActive: true,
        };

      case 'logicGate':
        return {
          gateType: subtype || 'AND',
          conditions: [],
          name: `${subtype || 'AND'} Gate`,
        };

      case 'wait':
        return {
          duration: 2,
          unit: 'days',
          description: '',
        };

      case 'entry':
        return {
          triggerType: 'system_event',
          eventType: 'customer_created',
          conditions: [],
          description: '',
        };

      case 'exit':
        return {
          label: 'Complete',
          exitType: 'success',
          actions: [],
          description: '',
        };

      default:
        return {};
    }
  };

  // Handle node selection
  // Don't select self-contained nodes (they have inline editing)
  const onNodeClick = useCallback((event, node) => {
    const selfContainedNodes = ['kanban', 'wait', 'exit', 'logicGate', 'entry'];
    if (selfContainedNodes.includes(node.type)) {
      setSelectedNode(null);
    } else {
      setSelectedNode(node);
    }
  }, []);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Handle node updates from properties panel
  const handleUpdateNode = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: newData } : node
      )
    );
  }, [setNodes]);

  // Handle flow change
  const handleFlowChange = useCallback((flow) => {
    console.log('📋 Loading flow:', flow.name);

    // Reset auto-saving flag when switching flows
    isAutoSavingRef.current = false;

    // Load flow details (which will trigger the selectedFlow effect)
    loadFlowDetails(flow.id);

    setSelectedNode(null);
  }, [loadFlowDetails]);

  // Handle create flow
  const handleCreateFlow = useCallback((flowData) => {
    console.log('➕ Creating new flow:', flowData.name);

    // Reset auto-saving flag
    isAutoSavingRef.current = false;

    // Create flow on backend via PipelineStore
    createFlow({
      name: flowData.name,
      description: flowData.description,
      flow_config: { nodes: [], edges: [] },
      is_active: true,
    });

    // Clear canvas for new flow
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
  }, [createFlow, setNodes, setEdges]);

  // Auto-save with debouncing - save flow config whenever nodes/edges change
  React.useEffect(() => {
    // Use ref to get current flow (prevents feedback loop)
    if (!currentFlowRef.current?.id) return;

    // Debounce auto-save by 2 seconds
    const autoSaveTimeout = setTimeout(() => {
      const flowConfig = { nodes, edges };

      console.log('💾 Auto-saving flow:', currentFlowRef.current.name);

      // Set flag to prevent the response from reloading nodes/edges
      isAutoSavingRef.current = true;

      // Save to backend via PipelineStore
      saveFlowConfig(currentFlowRef.current.id, flowConfig);
    }, 2000); // 2 second debounce

    return () => clearTimeout(autoSaveTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]); // Only depend on nodes/edges, not currentFlow or saveFlowConfig!

  // Watch for new pipeline creation and create kanban node
  React.useEffect(() => {
    if (pendingKanbanPosition && newPipelineName) {
      // Find the pipeline that was just created (matches the name we submitted)
      const newPipeline = pipelines.find(p => p.name === newPipelineName);

      if (newPipeline) {
        console.log('📋 Creating kanban node for new pipeline:', newPipeline.name);

        // Create kanban node at the pending position
        const newNode = {
          id: `kanban-${Date.now()}`,
          type: 'kanban',
          position: pendingKanbanPosition,
          data: {
            pipelineId: newPipeline.id,
            name: newPipeline.name,
            stages: newPipeline.stages || [],
            totalCustomers: newPipeline.customer_count || 0,
            isActive: newPipeline.is_active || true,
          },
        };

        setNodes((nds) => nds.concat(newNode));

        // Clear pending state
        setPendingKanbanPosition(null);
        setNewPipelineName('');
        setNewPipelineDescription('');
      }
    }
  }, [pipelines, pendingKanbanPosition, newPipelineName, setNodes]);

  // Sync pipeline data from store to React Flow nodes
  // When pipelines, stages, OR rules in the store update, update the corresponding kanban node data
  React.useEffect(() => {
    // Update all kanban nodes with latest pipeline data from store
    setNodes((currentNodes) => {
      return currentNodes.map((node) => {
        if (node.type === 'kanban' && node.data.pipelineId) {
          // Find matching pipeline in store
          const pipeline = pipelines.find(p => p.id === node.data.pipelineId);

          if (pipeline) {
            // Get stages for this pipeline from the stages array
            const pipelineStages = stages.filter(s => s.pipeline_id === node.data.pipelineId)
              .sort((a, b) => a.position - b.position)
              .map(stage => ({
                ...stage,
                // Add rule count for visual indicator
                ruleCount: rules.filter(r => r.stage_id === stage.id).length
              }));

            // Update node data with fresh pipeline data
            return {
              ...node,
              data: {
                ...node.data,
                name: pipeline.name,
                stages: pipelineStages,
                totalCustomers: pipeline.customer_count || 0,
                isActive: pipeline.is_active ?? node.data.isActive,
              },
            };
          }
        }
        return node;
      });
    });
  }, [pipelines, stages, rules, setNodes]); // Added rules to dependencies

  // Handle edit kanban (navigate to full kanban view)
  const handleEditKanban = useCallback((pipelineId) => {
    console.log('Edit kanban:', pipelineId);
    // TODO: Navigate to kanban detail page or open modal
  }, []);

  // Handle add node (from TopBar dropdown)
  const handleAddNode = useCallback((type, subtype = null, pipelineId = null, pipelineData = null, createNew = false) => {
    if (!reactFlowInstance) return;

    // If creating new pipeline, open dialog
    if (type === 'kanban' && createNew) {
      // Set a default position (center of viewport)
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: bounds.width / 2,
        y: bounds.height / 2,
      });
      setPendingKanbanPosition(position);
      setIsCreatePipelineDialogOpen(true);
      return;
    }

    // Calculate position (center of current viewport)
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = reactFlowInstance.screenToFlowPosition({
      x: bounds.width / 2,
      y: bounds.height / 2,
    });

    // Create new node
    const newNode = {
      id: `${type}-${Date.now()}`,
      type,
      position,
      data: getDefaultNodeData(type, subtype, pipelineId, pipelineData),
    };

    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes]);

  // Handle create pipeline from dialog
  const handleCreatePipelineSubmit = useCallback(() => {
    if (!newPipelineName.trim()) {
      console.error('Pipeline name is required');
      return;
    }

    console.log('Creating new pipeline:', newPipelineName);

    // Create pipeline via store (follows standard pattern - no await)
    createPipeline({
      name: newPipelineName,
      description: newPipelineDescription || '',
    });

    // Close dialog - the node will be created when the pipeline appears in the store
    // (via useEffect watching pipelines array)
    setIsCreatePipelineDialogOpen(false);
  }, [createPipeline, newPipelineName, newPipelineDescription]);

  // Handle cancel create pipeline dialog
  const handleCancelCreatePipeline = useCallback(() => {
    setIsCreatePipelineDialogOpen(false);
    setPendingKanbanPosition(null);
    setNewPipelineName('');
    setNewPipelineDescription('');
  }, []);

  return (
    <div className="w-full h-[calc(100vh-4rem)] bg-slate-950 react-flow-wrapper" ref={reactFlowWrapper}>
      <style>{`
        .react-flow-wrapper .react-flow__attribution {
          background: rgba(15, 23, 42, 0.9) !important;
          padding: 4px 8px !important;
          border-radius: 6px !important;
          border: 1px solid rgba(71, 85, 105, 0.5) !important;
          backdrop-filter: blur(8px) !important;
        }
        .react-flow-wrapper .react-flow__attribution a {
          color: #a78bfa !important;
          text-decoration: none !important;
          font-size: 10px !important;
        }
        .react-flow-wrapper .react-flow__attribution a:hover {
          color: #c4b5fd !important;
        }
      `}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        noDragClassName="no-drag"
        fitView
        attributionPosition="bottom-right"
        className="bg-slate-950"
      >
        {/* Background Grid */}
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#334155"
          className="bg-slate-950"
        />

        {/* Top Bar Overlay */}
        <TopBar
          flows={flows}
          currentFlow={currentFlow}
          onFlowChange={handleFlowChange}
          onCreateFlow={handleCreateFlow}
          pipelines={pipelines}
          onAddNode={handleAddNode}
        />

        {/* Properties Panel Overlay */}
        <PropertiesPanel
          selectedNode={selectedNode}
          onClose={() => setSelectedNode(null)}
          onUpdateNode={handleUpdateNode}
          onEditKanban={handleEditKanban}
        />
      </ReactFlow>

      {/* Create Pipeline Dialog */}
      <Dialog open={isCreatePipelineDialogOpen} onOpenChange={handleCancelCreatePipeline}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-slate-100">
              Create New Pipeline
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Pipeline Name */}
            <div className="space-y-2">
              <Label htmlFor="pipeline-name" className="text-slate-300">
                Pipeline Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="pipeline-name"
                placeholder="e.g., Sales Pipeline, Lead Nurture"
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
                autoFocus
              />
            </div>

            {/* Pipeline Description */}
            <div className="space-y-2">
              <Label htmlFor="pipeline-description" className="text-slate-300">
                Description (optional)
              </Label>
              <Input
                id="pipeline-description"
                placeholder="Brief description of this pipeline"
                value={newPipelineDescription}
                onChange={(e) => setNewPipelineDescription(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelCreatePipeline}
              className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePipelineSubmit}
              disabled={!newPipelineName.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Create Pipeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowCanvasShadcn;
